import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const styles = {
  container: {
    marginBottom: '20px',
  },
  explanationText: {
    fontSize: '12px',
    color: '#666666',
    lineHeight: '1.4',
    marginBottom: '20px',
    fontFamily: 'Poppins, sans-serif',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  chartContainer: {
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '15px',
  },
  phaseTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#333333',
    fontFamily: 'Poppins, sans-serif',
    textAlign: 'center',
  },
  chartWrapper: {
    height: '200px',
    position: 'relative',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '15px',
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendColor: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  legendText: {
    fontSize: '9px',
    fontFamily: 'Poppins, sans-serif',
    color: '#666666',
  },
};

// Category colors
const categoryColors = {
  skills: '#4CAF50',      // Green
  knowledge: '#2196F3',   // Blue  
  mindset: '#FF9800',     // Orange
  instruments: '#9C27B0', // Purple
};

// Session colors
const sessionColors = {
  session1: '#EC4899',    // Pink
  session2: '#06B6D4',    // Cyan
};

const PHASES = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];

const ComparisonSessionDistributionWeb = ({ session1Responses = [], session2Responses = [], allCompetencies = [] }) => {
  // Process data to get phase-based breakdown for both sessions
  const phaseData = useMemo(() => {
    const data = {};
    
    // Initialize each phase
    PHASES.forEach(phase => {
      data[phase] = {
        skills: { session1: 0, session2: 0 },
        knowledge: { session1: 0, session2: 0 },
        mindset: { session1: 0, session2: 0 },
        instruments: { session1: 0, session2: 0 },
      };
    });

    // Get competency IDs for each session
    const session1CompetencyIds = new Set(session1Responses?.map(response => response.competency_id) || []);
    const session2CompetencyIds = new Set(session2Responses?.map(response => response.competency_id) || []);

    // Process all competencies
    allCompetencies.forEach(comp => {
      const phase = comp.abilities?.phase || 'Unknown';
      const category = comp.category?.toLowerCase();
      
      if (PHASES.includes(phase) && category && data[phase][category]) {
        const inSession1 = session1CompetencyIds.has(comp.id);
        const inSession2 = session2CompetencyIds.has(comp.id);
        
        if (inSession1) {
          data[phase][category].session1++;
        }
        if (inSession2) {
          data[phase][category].session2++;
        }
      }
    });

    return data;
  }, [session1Responses, session2Responses, allCompetencies]);

  const renderPhaseChart = (phase) => {
    const data = phaseData[phase];
    const categories = ['skills', 'knowledge', 'mindset', 'instruments'];
    
    const chartData = {
      labels: categories.map(cat => cat.charAt(0).toUpperCase() + cat.slice(1)),
      datasets: [
        {
          label: 'Session 1',
          data: categories.map(cat => data[cat].session1),
          backgroundColor: categories.map(cat => categoryColors[cat]), // Solid color
          borderColor: categories.map(cat => categoryColors[cat]),
          borderWidth: 2,
        },
        {
          label: 'Session 2',
          data: categories.map(cat => data[cat].session2),
          backgroundColor: categories.map(cat => `${categoryColors[cat]}60`), // 37.5% opacity
          borderColor: categories.map(cat => categoryColors[cat]),
          borderWidth: 2,
          borderDash: [5, 5], // Dashed border for session 2
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // We'll use our custom legend
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const session = context.dataset.label;
              const category = context.label;
              const count = context.parsed.y;
              return `${session}: ${count} competencies`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            font: {
              family: 'Poppins, sans-serif',
              size: 10,
            }
          },
          grid: {
            color: '#e0e0e0',
          }
        },
        x: {
          ticks: {
            font: {
              family: 'Poppins, sans-serif',
              size: 10,
            }
          },
          grid: {
            display: false,
          }
        }
      }
    };

    return (
      <div style={styles.chartContainer}>
        <div style={styles.phaseTitle}>{phase}</div>
        <div style={styles.chartWrapper}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.explanationText}>
        Shows the number of competencies in each category for each design thinking phase, comparing Session 1 and Session 2. Each chart represents one phase, with grouped bars showing the count of competencies in each category (Skills, Knowledge, Mindset, Instruments) for both sessions. Solid bars represent Session 1, dashed bars represent Session 2.
      </div>
      
      <div style={styles.chartsGrid}>
        {PHASES.map(phase => (
          <div key={phase}>
            {renderPhaseChart(phase)}
          </div>
        ))}
      </div>

      <div style={styles.legend}>
        {Object.entries(categoryColors).map(([category, color]) => (
          <div key={category} style={styles.legendItem}>
            <div style={{
              ...styles.legendColor,
              backgroundColor: color,
            }} />
            <div style={styles.legendText}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </div>
          </div>
        ))}
        <div style={{ ...styles.legendItem, marginLeft: '15px' }}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: '#333',
            border: '2px solid #333',
          }} />
          <div style={styles.legendText}>Session 1</div>
        </div>
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: 'rgba(51, 51, 51, 0.6)',
            border: '2px dashed #333',
          }} />
          <div style={styles.legendText}>Session 2</div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonSessionDistributionWeb;
